import {
    Ref,
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from "react";

import useAppStateSync from "@hooks/useAppStateSync";
import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useEnrolPageProps from "@hooks/useEnrolPageProps";

import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { LOCAL_STORAGE_SUBJECTS } from "@/ts/constants";
import { dispatchGAEvents } from "@/utils/events/dispatchGAEvents";
import { removeLocalStorageItem } from "@/utils/localStorageUtils";
import {
    CardCVV,
    CardComponent,
    CardExpiry,
    CardNumber
} from "@chargebee/chargebee-js-react-wrapper";
import CircularPlusIcon from "@icons/CircularPlusIcon";
import { AxiosError } from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { t } from "i18next";
import { useAtom, useSetAtom } from "jotai";

import {
    AbsoluteCenter,
    Box,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Input
} from "@chakra-ui/react";

import { CheckoutFlowEnum } from "@ts/enums/CheckoutFlowEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { BillingDetailsInterface } from "@ts/interfaces/Checkout/BillingDetailsInterface";
import { ExistingCardApiInterface } from "@ts/interfaces/Checkout/api/ExistingCardsApiInterface";

import {
    addAddonAtom,
    addSubjectChangesAtom,
    checkoutAtom,
    couponAtom,
    createPaymentIntentAtom,
    createPaymentIntentForAddonAtom,
    createPaymentIntentForSubjectChangesAtom,
    createSubscriptionAtom,
    isNewsletterSubEnabledAtom,
    isPaymentPendingAtom,
    parentIdAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom";

import { dispatchHSEvents } from "@utils/events/dispatchHSEevents";
import { getTrackingId } from "@utils/utils";

import { Text } from "@components/Text";

import { PriceIdApiInterface } from "../../PaymentCaptureContainer";
import ExistingCard from "./components/ExistingCard";

interface CardChangeEvent {
    field: "number" | "cvv" | "expiry";
    complete: boolean;
    error?: { errorCode: string; message: string };
}

export interface CardPaymentContainerRef {
    initiateCardPayment: () => Promise<void>;
}

interface PropTypes {
    isHundredPercentOff: boolean;
    billingDetails: BillingDetailsInterface;
    priceIds: PriceIdApiInterface[];
    shouldCaptureBillingInfo: boolean;
    onDispatchGAEvents: () => void;
    onTogglePaymentEnable: (e: boolean) => void;
    trackWithHeap: (event: string, data?: object) => void;
    onSetPaymentError: (error: { hasError: boolean; message: string }) => void;
    onAddNewPaymentMethod: (e: boolean) => void;
}

const CardPaymentContainer = forwardRef(
    (
        {
            isHundredPercentOff,
            billingDetails,
            priceIds,
            shouldCaptureBillingInfo,
            onDispatchGAEvents,
            onTogglePaymentEnable,
            onSetPaymentError,
            trackWithHeap,
            onAddNewPaymentMethod
        }: PropTypes,
        ref: Ref<CardPaymentContainerRef>
    ) => {
        const cardRef = useRef(null);

        const [coupon] = useAtom(couponAtom);

        const {
            hubspotEventCheckout,
            existingCards = [],
            parentUser
        } = useCheckoutPageProps();
        const { hubspotEventEnrol } = useEnrolPageProps();

        const [cardPaymentError, setCardPaymentError] = useState<{
            hasError: boolean;
            message: string;
        }>({ hasError: false, message: "" });
        const [isCardInputReady, setIsCardInputReady] =
            useState<boolean>(false);
        const [presentCardErrors, setPresentCardErrors] = useState({
            number: null,
            expiry: null,
            cvv: null
        });
        const [presentCardCompletions, setPresetCardCompletions] = useState({
            number: false,
            expiry: false,
            cvv: false
        });
        const [cardNameInput, setCardNameInput] = useState("");

        const [checkout] = useAtom(checkoutAtom);
        // Pending payment state is consumed globally
        const setIsPaymentPending = useSetAtom(isPaymentPendingAtom);
        const [, createPaymentIntentMutate] = useAtom(createPaymentIntentAtom);
        const [, addAddonMutate] = useAtom(addAddonAtom);
        const [, addSubjectChangesMutate] = useAtom(addSubjectChangesAtom);
        const [, createPaymentIntentForAddonMutate] = useAtom(
            createPaymentIntentForAddonAtom
        );
        const [, createPaymentIntentForSubjectChangesMutate] = useAtom(
            createPaymentIntentForSubjectChangesAtom
        );

        const [, createSubscriptionMutate] = useAtom(createSubscriptionAtom);
        const [userCheckoutStateToken] = useAtom(userCheckoutStateTokenAtom);

        const [isNewsletterSubEnabled] = useAtom(isNewsletterSubEnabledAtom);

        const [parentId] = useAtom(parentIdAtom);

        const [canAddNewCard, setCanAddNewCard] = useState(
            shouldCaptureBillingInfo
        );
        const [selectedExistingCard, setSelectedExistingCard] =
            useState<ExistingCardApiInterface | null>(null);

        const {
            state: { checkout: checkoutAppState }
        } = useAppStateSync();

        const checkoutContext = useContext(CheckoutContext);

        const { userSelectedCountry, userSelectedState, userSelectedLang } =
            checkoutContext;

        const onCardInputReady = () => setIsCardInputReady(true);

        const onCardInputChange = (event: CardChangeEvent) => {
            setPresetCardCompletions(prevValue => ({
                ...prevValue,
                [event.field]: event.complete
            }));
            if (event.error) {
                console.log(`CARD ERROR: ${event.error.message}`);
                setPresentCardErrors(prevValue => ({
                    ...prevValue,
                    [event.field]: event.error!.message
                }));
            } else {
                setPresentCardErrors(prevValue => ({
                    ...prevValue,
                    [event.field]: null
                }));
            }
        };

        const checkoutFlow = checkoutAppState?.checkoutFlow ?? null;

        const removeSubjectsFromLocalStorage = (
            studentId: number | null,
            pathway: string | undefined
        ) => {
            removeLocalStorageItem(
                `${LOCAL_STORAGE_SUBJECTS}_${studentId}_${pathway}`
            );
        };

        const onCardNameInputChange = (v: string) => setCardNameInput(v);

        const onSelectExistingCard = (id: string) => {
            resetCardPaymentError();
            onAddNewPaymentMethod(false);
            const card = existingCards.find(c => c.id === id) ?? null;
            setCanAddNewCard(false);
            setSelectedExistingCard(card);
            setPresentCardErrors({
                cvv: null,
                expiry: null,
                number: null
            });
            setPresetCardCompletions({
                cvv: false,
                expiry: false,
                number: false
            });
            setIsCardInputReady(false);
            setCardNameInput("");
        };

        const onAddNewCard = () => {
            resetCardPaymentError();
            setCanAddNewCard(true);
            setSelectedExistingCard(null);
            onAddNewPaymentMethod(true);
        };

        const resetCardPaymentError = () => {
            // Reset the error message
            setCardPaymentError({ hasError: false, message: "" });
        };

        const initiateCardPaymentForSubscription = async () => {
            try {
                setIsPaymentPending(true);
                setCardPaymentError({ hasError: false, message: "" });

                let paymentIntentIdVal: string | null = null;
                let gatewayAccountIdVal: string | null = null;
                let gwToken: string | null = null;

                if (!isHundredPercentOff) {
                    // Get a payment intent created from backend to get it authorized
                    const { data: paymentIntent } =
                        await createPaymentIntentMutate([
                            {
                                type: "intent",
                                currency: checkout.plan?.currency ?? "AUD",
                                firstName: checkout.user!.firstName,
                                email: checkout.user!.email,
                                priceIds,
                                ...(coupon ? { coupon } : {}),
                                selectedCountry: userSelectedCountry,
                                selectedState: userSelectedState,
                                selectedLang: userSelectedLang,
                                ...(shouldCaptureBillingInfo
                                    ? { billingDetails }
                                    : {})
                            }
                        ]);

                    if (cardRef.current) {
                        // Initiate 3DS authorization against the card details provided, and get the payment intent authorized.
                        const response = await (
                            cardRef.current as {
                                authorizeWith3ds: (
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    pi: any,
                                    additionalData: {
                                        firstName: string;
                                    }
                                ) => Promise<{
                                    active_payment_attempt: {
                                        id_at_gateway: string;
                                    };
                                }>;
                            }
                        ).authorizeWith3ds(paymentIntent, {
                            firstName: cardNameInput
                        });
                        paymentIntentIdVal = paymentIntent.id;
                        gatewayAccountIdVal = paymentIntent.gateway_account_id;
                        gwToken = response.active_payment_attempt.id_at_gateway;

                        console.log("3Ds Authorization response", response);
                    }
                }

                // Create subscription based on the price IDs
                await createSubscriptionMutate([
                    {
                        type: "subscription",
                        itemType: "plan",
                        firstName: checkout.user!.firstName,
                        email: checkout.user!.email,
                        gwToken,
                        priceIds,
                        paymentIntent: paymentIntentIdVal,
                        gatewayAccountId: gatewayAccountIdVal,
                        gradeId: checkout.grade!.id,
                        studentId:
                            checkoutAppState?.checkoutStudentId ??
                            checkout.selectedStudent?.id ??
                            null,
                        parentId: parentUser?.id ?? null,
                        termType: checkout.plan!.period,
                        ...(coupon ? { coupon } : {}),
                        selectedCountry: userSelectedCountry,
                        selectedState: userSelectedState,
                        selectedLang: userSelectedLang,
                        ...(shouldCaptureBillingInfo ? { billingDetails } : {})
                    }
                ]);
                trackWithHeap("Purchase");

                const navigationEntries =
                    performance.getEntriesByType("navigation");
                let navigationEntryType = "navigate";

                if (navigationEntries.length > 0) {
                    const navigationEntry =
                        navigationEntries[0] as PerformanceNavigationTiming;
                    navigationEntryType = navigationEntry.type;
                }

                const isPageAccessedByReload =
                    (window.performance.navigation &&
                        window.performance.navigation.type === 1) ||
                    navigationEntryType?.includes("reload");

                if (!isPageAccessedByReload) {
                    // fire e-commerce event
                    onDispatchGAEvents();
                }
            } catch (error) {
                let errorCode = "";
                let errorMessage = "";
                if (error instanceof AxiosError) {
                    // Handle errors thrown by the subscription API
                    errorCode = error.response
                        ? (error.response.data as { message: string }).message
                        : error.message;
                    errorMessage = `Payment was unsuccessful. Please check your payment details and ensure you have sufficient funds in your account before trying again. (Failed payment reason: ${errorCode})`;
                    setCardPaymentError({
                        hasError: true,
                        message: errorMessage
                    });
                    dispatchHSEvents(
                        "trackCustomBehavioralEvent",
                        undefined,
                        {
                            checkout_status: "FAILED",
                            checkout_error: errorCode,
                            selected_country: userSelectedCountry,
                            hs_language: userSelectedLang,
                            email: checkout.user.email,
                            checkout_token: userCheckoutStateToken,
                            newsletter_subscription: isNewsletterSubEnabled
                                ? "yes"
                                : "no"
                        },
                        "checkout",
                        hubspotEventCheckout,
                        hubspotEventEnrol
                    );
                    console.log("SUBSCRIPTION API ERROR:", errorMessage);
                } else {
                    errorMessage =
                        "Payment was unsuccessful. Please check your payment details and ensure you have sufficient funds in your account before trying again.";
                    // Handle common errors thrown by Chargebee, etc
                    setCardPaymentError({
                        hasError: true,
                        message: errorMessage
                    });
                    console.log(errorCode);
                    dispatchHSEvents(
                        "trackCustomBehavioralEvent",
                        undefined,
                        {
                            checkout_status: "FAILED",
                            checkout_error: (error as { message: string })
                                .message,
                            selected_country: userSelectedCountry,
                            hs_language: userSelectedLang,
                            email: checkout.user.email,
                            checkout_token: userCheckoutStateToken,
                            newsletter_subscription: isNewsletterSubEnabled
                                ? "yes"
                                : "no"
                        },
                        "checkout",
                        hubspotEventCheckout,
                        hubspotEventEnrol
                    );
                    console.log(errorMessage);

                    dispatchGAEvents({
                        event: "drop_off",
                        eventCategory: "checkout",
                        eventAction: "payment_unsuccessful",
                        eventLabel: "Payment Details",
                        parentId
                    });
                }
                trackWithHeap("payment_error", { errorMessage });
            } finally {
                setIsPaymentPending(false);
            }
        };

        // eslint-disable-next-line complexity
        const initiateCardPaymentForCharge = async (
            chargeType: "addon" | "subjectChanges"
        ) => {
            try {
                setIsPaymentPending(true);
                setCardPaymentError({ hasError: false, message: "" });

                let paymentIntentIdVal: string | null = null;
                let gatewayAccountIdVal: string | null = null;
                let gwToken: string | null = null;

                if (cardRef.current && !isHundredPercentOff) {
                    // Get a payment intent created from backend to get it authorized
                    let paymentIntent: {
                        id: string;
                        gateway_account_id: string;
                    } | null = null;

                    if (chargeType === "addon") {
                        const { data } =
                            await createPaymentIntentForAddonMutate([
                                {
                                    type: "intent",
                                    currency: checkout.plan?.currency ?? "AUD",
                                    firstName: checkout.user!.firstName,
                                    email: checkout.user!.email,
                                    customerId:
                                        checkoutAppState?.checkoutStudentCBId ??
                                        "",
                                    priceIds: checkout.addon?.priceId,
                                    ...(coupon ? { coupon } : {}),
                                    ...(shouldCaptureBillingInfo
                                        ? { billingDetails }
                                        : {})
                                }
                            ]);
                        paymentIntent = data;
                    }

                    if (chargeType === "subjectChanges") {
                        const { data } =
                            await createPaymentIntentForSubjectChangesMutate([
                                {
                                    type: "intent",
                                    currency: checkout.plan?.currency ?? "AUD",
                                    itemQty:
                                    checkoutAppState!.checkoutSubjectsFee!
                                        .quantity,
                                    firstName: checkout.user!.firstName,
                                    email: checkout.user!.email,
                                    customerId:
                                        checkoutAppState?.checkoutStudentCBId ??
                                        "",
                                    priceIds:
                                    checkoutAppState!.checkoutSubjectsFee!
                                        .priceId,
                                    subjectIds:
                                    checkoutAppState!.checkoutSubjectsFee!
                                        .subjectIds,
                                    selectedLicense:
                                    checkoutAppState!.checkoutSubjectsFee
                                        ?.selectedLicense,
                                    ...(coupon ? { coupon } : {}),
                                    ...(shouldCaptureBillingInfo
                                        ? { billingDetails }
                                        : {})
                                }
                            ]);
                        paymentIntent = data;
                    }

                    // Initiate 3DS authorization against the card details provided, and get the payment intent authorized
                    const response = await (
                        cardRef.current as {
                            authorizeWith3ds: (
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                pi: any,
                                additionalData: {
                                    firstName: string;
                                }
                            ) => Promise<{
                                active_payment_attempt: {
                                    id_at_gateway: string;
                                };
                            }>;
                        }
                    ).authorizeWith3ds(paymentIntent, {
                        firstName: cardNameInput
                    });
                    paymentIntentIdVal = paymentIntent!.id;
                    gatewayAccountIdVal = paymentIntent!.gateway_account_id;
                    gwToken = response.active_payment_attempt.id_at_gateway;

                    console.log("3Ds Authorization response", response);
                }

                if (chargeType === "addon") {
                    await addAddonMutate([
                        {
                            type: "charge",
                            customerId:
                                checkoutAppState?.checkoutStudentCBId ?? "",
                            exist_card: false,
                            itemPriceId: checkout.addon?.priceId,
                            gatewayAccountId: gatewayAccountIdVal!,
                            gwToken: gwToken!,
                            paymentIntent: paymentIntentIdVal!,
                            ...(coupon ? { coupon } : {}),
                            ...(shouldCaptureBillingInfo
                                ? { billingDetails }
                                : {})
                        }
                    ]);
                }

                if (chargeType === "subjectChanges") {
                    await addSubjectChangesMutate([
                        {
                            type: "charge",
                            customerId:
                                checkoutAppState?.checkoutStudentCBId ?? "",
                            exist_card: false,
                            itemPriceId:
                            checkoutAppState!.checkoutSubjectsFee!.priceId,
                            itemQty:
                            checkoutAppState!.checkoutSubjectsFee!.quantity,
                            subjectIds:
                            checkoutAppState!.checkoutSubjectsFee!
                                .subjectIds,
                            selectedLicense:
                            checkoutAppState!.checkoutSubjectsFee
                                ?.selectedLicense,
                            gatewayAccountId: gatewayAccountIdVal!,
                            gwToken: gwToken!,
                            paymentIntent: paymentIntentIdVal!,
                            ...(coupon ? { coupon } : {}),
                            ...(shouldCaptureBillingInfo
                                ? { billingDetails }
                                : {})
                        }
                    ]);

                    removeSubjectsFromLocalStorage(
                        checkoutAppState?.checkoutStudentId ?? null,
                        checkoutAppState!.checkoutSubjectsFee?.pathway
                    );
                }

                trackWithHeap("Purchase");
                // fire e-commerce event
                onDispatchGAEvents();
            } catch (error) {
                let errorCode = "";
                let errorMessage = "";
                if (error instanceof AxiosError) {
                    // Handle errors thrown by the subscription API
                    errorCode = error.response
                        ? (error.response.data as { message: string }).message
                        : error.message;
                    errorMessage = `Payment was unsuccessful. Please check your payment details and ensure you have sufficient funds in your account before trying again. (Failed payment reason: ${errorCode})`;
                    setCardPaymentError({
                        hasError: true,
                        message: errorMessage
                    });
                    dispatchHSEvents(
                        "trackCustomBehavioralEvent",
                        undefined,
                        {
                            checkout_status: "FAILED",
                            checkout_error: errorCode,
                            email: checkout.user.email,
                            selected_country: userSelectedCountry,
                            hs_language: userSelectedLang,
                            checkout_token: userCheckoutStateToken,
                            newsletter_subscription: isNewsletterSubEnabled
                                ? "yes"
                                : "no"
                        },
                        "checkout",
                        hubspotEventCheckout,
                        hubspotEventEnrol
                    );
                    console.log("SUBSCRIPTION API ERROR:", errorMessage);
                } else {
                    errorMessage =
                        "Payment was unsuccessful. Please check your payment details and ensure you have sufficient funds in your account before trying again.";
                    // Handle common errors thrown by Chargebee, etc
                    setCardPaymentError({
                        hasError: true,
                        message: errorMessage
                    });
                    console.log(errorCode);
                    dispatchHSEvents(
                        "trackCustomBehavioralEvent",
                        undefined,
                        {
                            checkout_status: "FAILED",
                            checkout_error: (error as { message: string })
                                .message,
                            selected_country: userSelectedCountry,
                            hs_language: userSelectedLang,
                            email: checkout.user.email,
                            checkout_token: userCheckoutStateToken,
                            newsletter_subscription: isNewsletterSubEnabled
                                ? "yes"
                                : "no"
                        },
                        "checkout",
                        hubspotEventCheckout,
                        hubspotEventEnrol
                    );
                    console.log(errorMessage);
                }

                trackWithHeap("payment_error", { errorMessage });
            } finally {
                setIsPaymentPending(false);
            }
        };

        const initiatePaymentForChargeWithExistingCard = async (
            chargeType: "addon" | "subjectChanges"
        ) => {
            try {
                setIsPaymentPending(true);

                if (chargeType === "addon") {
                    await addAddonMutate([
                        {
                            type: "charge",
                            customerId:
                                checkoutAppState?.checkoutStudentCBId ?? "",
                            exist_card: true,
                            itemPriceId: checkout.addon?.priceId,
                            paymentSourceId: selectedExistingCard?.id,
                            ...(coupon ? { coupon } : {}),
                            ...(shouldCaptureBillingInfo
                                ? { billingDetails }
                                : {})
                        }
                    ]);
                }

                if (chargeType === "subjectChanges") {
                    await addSubjectChangesMutate([
                        {
                            type: "charge",
                            customerId:
                                checkoutAppState?.checkoutStudentCBId ?? "",
                            itemQty:
                            checkoutAppState!.checkoutSubjectsFee!.quantity,
                            itemPriceId:
                            checkoutAppState!.checkoutSubjectsFee!.priceId,
                            exist_card: true,
                            paymentSourceId: selectedExistingCard?.id,
                            subjectIds:
                            checkoutAppState!.checkoutSubjectsFee!
                                .subjectIds,
                            selectedLicense:
                            checkoutAppState!.checkoutSubjectsFee
                                ?.selectedLicense,
                            ...(coupon ? { coupon } : {}),
                            ...(shouldCaptureBillingInfo
                                ? { billingDetails }
                                : {})
                        }
                    ]);

                    removeSubjectsFromLocalStorage(
                        checkoutAppState?.checkoutStudentId ?? null,
                        checkoutAppState!.checkoutSubjectsFee?.pathway
                    );
                }

                trackWithHeap("Purchase");
                // fire e-commerce event
                onDispatchGAEvents();
            } catch (error) {
                let errorCode = "";
                let errorMessage = "";
                if (error instanceof AxiosError) {
                    // Handle errors thrown by the subscription API
                    errorCode = error.response
                        ? (error.response.data as { message: string }).message
                        : error.message;
                    errorMessage = `Payment was unsuccessful. Please check your payment details and ensure you have sufficient funds in your account before trying again. (Failed payment reason: ${errorCode})`;
                    setCardPaymentError({
                        hasError: true,
                        message: errorMessage
                    });
                    dispatchHSEvents(
                        "trackCustomBehavioralEvent",
                        undefined,
                        {
                            checkout_status: "FAILED",
                            checkout_error: errorCode,
                            email: checkout.user.email,
                            selected_country: userSelectedCountry,
                            hs_language: userSelectedLang,
                            checkout_token: userCheckoutStateToken,
                            newsletter_subscription: isNewsletterSubEnabled
                                ? "yes"
                                : "no"
                        },
                        "checkout",
                        hubspotEventCheckout,
                        hubspotEventEnrol
                    );
                    console.log("SUBSCRIPTION API ERROR:", errorMessage);
                } else {
                    errorMessage =
                        "Payment was unsuccessful. Please check your payment details and ensure you have sufficient funds in your account before trying again.";
                    // Handle common errors
                    setCardPaymentError({
                        hasError: true,
                        message: errorMessage
                    });
                    console.log(errorCode);
                    dispatchHSEvents(
                        "trackCustomBehavioralEvent",
                        undefined,
                        {
                            checkout_status: "FAILED",
                            checkout_error: (error as { message: string })
                                .message,
                            email: checkout.user.email,
                            selected_country: userSelectedCountry,
                            hs_language: userSelectedLang,
                            checkout_token: userCheckoutStateToken,
                            newsletter_subscription: isNewsletterSubEnabled
                                ? "yes"
                                : "no"
                        },
                        "checkout",
                        hubspotEventCheckout,
                        hubspotEventEnrol
                    );
                    console.log(errorMessage);
                }
                setCardPaymentError({
                    hasError: true,
                    message: errorMessage
                });

                trackWithHeap("payment_error", { errorMessage });
            } finally {
                setIsPaymentPending(false);
            }
        };

        const existingCardsByCurrentStudent = existingCards.filter(card => {
            if (checkout.selectedStudent) {
                return card.student_id === checkout.selectedStudent.id;
            }
            return card.student_id === checkoutAppState?.checkoutStudentId;
        });

        const canPayWithExistingCards =
            existingCardsByCurrentStudent.length > 0;

        const initiateCardPayment = async () => {
            resetCardPaymentError();

            if (checkoutFlow === CheckoutFlowEnum.PREMIUM_SERVICE_PAYMENT) {
                if (canPayWithExistingCards && selectedExistingCard) {
                    await initiatePaymentForChargeWithExistingCard("addon");
                } else {
                    await initiateCardPaymentForCharge("addon");
                }
                return;
            }

            if (checkoutFlow === CheckoutFlowEnum.SUBJECT_CHANGE_FEE_PAYMENT) {
                if (canPayWithExistingCards && selectedExistingCard) {
                    await initiatePaymentForChargeWithExistingCard(
                        "subjectChanges"
                    );
                } else {
                    await initiateCardPaymentForCharge("subjectChanges");
                }
                return;
            }
            await initiateCardPaymentForSubscription();
        };

        const isCardInputsValid =
            Object.values(presentCardErrors).every(e => e === null) &&
            Object.values(presentCardCompletions).every(c => c);

        const renderCardDetailsInput = () => {
            return (
                <Flex direction="column" w="full" gap={6}>
                    <Flex direction="column" w="full">
                        <FormLabel fontSize={14}>
                            {t(
                                "checkout.checkoutPage.cardInfoLabel",
                                "Card information"
                            )}
                        </FormLabel>
                        <CardComponent
                            styles={{
                                base: {
                                    // eslint-disable-next-line quote-props
                                    color: "#1A202C",
                                    "::placeholder": {
                                        color: "#B5B5BD"
                                    }
                                },
                                invalid: {
                                    color: "#1A202C"
                                }
                            }}
                            classes={{ focus: "focus", invalid: "invalid" }}
                            fonts={["ballinger", "sans-serif"]}
                            ref={cardRef}
                            onReady={onCardInputReady}
                            onChange={e =>
                                onCardInputChange(
                                    e as unknown as CardChangeEvent
                                )
                            }
                        >
                            <Flex
                                borderRadius={5}
                                direction="column"
                                fontSize={15}
                                className="card"
                                position="relative"
                                _before={
                                    isHundredPercentOff && coupon
                                        ? {
                                            content: "\"\"",
                                            w: "full",
                                            h: "full",
                                            position: "absolute",
                                            bgColor: "gray.300",
                                            opacity: "0.7"
                                        }
                                        : {}
                                }
                            >
                                <Box
                                    w="full"
                                    h="full"
                                    className={ClassesEnum.CardInput}
                                    data-tracking-id={getTrackingId(
                                        [
                                            ClassesEnum.CardDetailsContainer,
                                            ClassesEnum.CardInput
                                        ],
                                        "inputCardNumber"
                                    )}
                                    data-testid="card-name"
                                >
                                    <CardNumber
                                        className="field number"
                                        placeholder="1234 1234 1234 1234"
                                    />
                                </Box>
                                <Flex w="100%">
                                    <Box
                                        w="full"
                                        h="full"
                                        className={ClassesEnum.CardInput}
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.CardDetailsContainer,
                                                ClassesEnum.CardInput
                                            ],
                                            "inputCardExpiryDate"
                                        )}
                                    >
                                        <CardExpiry
                                            className="field expiry"
                                            placeholder="MM / YY"
                                        />
                                    </Box>
                                    <Box
                                        w="full"
                                        h="full"
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.CardDetailsContainer,
                                                ClassesEnum.CardInput
                                            ],
                                            "inputCardCVV"
                                        )}
                                    >
                                        <CardCVV
                                            className="field cvv"
                                            placeholder="CVV"
                                        />
                                    </Box>
                                </Flex>
                            </Flex>
                        </CardComponent>
                    </Flex>
                    <FormControl
                        fontSize={15}
                        className={ClassesEnum.FormField}
                        data-tracking-id={getTrackingId(
                            [
                                ClassesEnum.CardDetailsContainer,
                                ClassesEnum.FormField
                            ],
                            "inputCardName"
                        )}
                    >
                        <FormLabel fontSize={14}>
                            {t("NameOnCard", "Name on card")}
                        </FormLabel>
                        <Input
                            className={ClassesEnum.Input}
                            data-tracking-id={getTrackingId(
                                [
                                    ClassesEnum.CardDetailsContainer,
                                    ClassesEnum.FormField,
                                    ClassesEnum.Input
                                ],
                                "inputCardName"
                            )}
                            value={cardNameInput}
                            onChange={e =>
                                onCardNameInputChange(e.target.value)
                            }
                            id="cardName"
                            maxLength={200}
                            isDisabled={isHundredPercentOff}
                            _disabled={
                                isHundredPercentOff && coupon
                                    ? {
                                        content: "\"\"",
                                        bgColor: "gray.300",
                                        opacity: "0.7"
                                    }
                                    : {}
                            }
                        />
                    </FormControl>
                </Flex>
            );
        };

        useImperativeHandle(ref, () => ({
            initiateCardPayment
        }));

        // Notify parent component about the payment ready state
        useEffect(() => {
            onTogglePaymentEnable(
                ((isCardInputReady || isHundredPercentOff) &&
                    (isCardInputsValid || isHundredPercentOff) &&
                    (!!cardNameInput || isHundredPercentOff)) ||
                !!selectedExistingCard
            );
        }, [
            isCardInputReady,
            isCardInputsValid,
            cardNameInput,
            selectedExistingCard,
            isHundredPercentOff
        ]);

        // Notify parent component about the payment error
        useEffect(() => {
            onSetPaymentError(cardPaymentError);
        }, [cardPaymentError]);

        useEffect(() => {
            if (isHundredPercentOff) {
                setSelectedExistingCard(null);
                onAddNewCard();
            }
        }, [isHundredPercentOff]);

        if (canPayWithExistingCards)
            return (
                <>
                    <Text
                        fontFamily="infini, sans-serif"
                        fontWeight="bold"
                        fontSize={16}
                        mb={-3}
                    >
                        {t("selectPaymentMethod", "Select a payment method")}
                    </Text>
                    <Flex direction="column" w="full" gap={[4, null, null, 6]}>
                        {existingCardsByCurrentStudent.map(c => {
                            return (
                                <ExistingCard
                                    key={c.id}
                                    cardBrand={c.brand}
                                    cardId={c.id}
                                    isDisabled={isHundredPercentOff}
                                    isSelected={
                                        selectedExistingCard?.id === c.id
                                    }
                                    maskedNumber={c.masked_number}
                                    onSelectExistingCard={onSelectExistingCard}
                                />
                            );
                        })}
                        {!canAddNewCard && (
                            <>
                                <Flex
                                    gap={2}
                                    align="center"
                                    userSelect="none"
                                    cursor="pointer"
                                    onClick={onAddNewCard}
                                >
                                    <CircularPlusIcon
                                        color="var(--euka-colors-royalBlue-500)"
                                        width={20}
                                        height={20}
                                    />
                                    <Text
                                        color="royalBlue.500"
                                        fontFamily="infini, sans-serif"
                                        fontWeight="bold"
                                        fontSize={14}
                                    >
                                        {t(
                                            "addNewPaymentMethod",
                                            "Add new payment method"
                                        )}
                                    </Text>
                                </Flex>
                                <Divider borderColor="gray.400" />
                            </>
                        )}
                        <AnimatePresence mode="popLayout">
                            {canAddNewCard && (
                                <motion.div
                                    style={{ overflow: "clip" }}
                                    initial={{ height: 0, opacity: 0, y: -10 }}
                                    animate={{
                                        height: "100%",
                                        opacity: 1,
                                        y: 0
                                    }}
                                    exit={{
                                        height: 0,
                                        opacity: 0,
                                        y: -1
                                    }}
                                    transition={{
                                        type: "tween",
                                        duration: 0.1,
                                        ease: "easeOut"
                                    }}
                                >
                                    <Box position="relative" w="full" my={4}>
                                        <Divider borderColor="gray.400" />
                                        <AbsoluteCenter>
                                            <Text
                                                fontSize={14}
                                                color="gray.500"
                                                bg="white"
                                                px={2}
                                            >
                                                {t(
                                                    "addANewPaymentMethod",
                                                    "Add a new payment method"
                                                )}
                                            </Text>
                                        </AbsoluteCenter>
                                    </Box>
                                    {renderCardDetailsInput()}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Flex>
                </>
            );

        return <>{renderCardDetailsInput()}</>;
    }
);

export default CardPaymentContainer;
